import { useState, type ReactNode } from 'react';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { CreditCard, ArrowDown, ArrowUp } from 'lucide-react';
import { endOfDay, format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { membershipPlanKeys } from '@/lib/queryKeys';
import {
  formatCurrency,
  formatMembershipPlanCustomRatesSummary,
  formatMembershipPlanDiscount,
  hasMembershipPlanDiscount,
  membershipPlanBillingAmount,
  membershipPlanDiscountPercent,
  membershipPlanBillingFrequency,
  formatBillingIntervalShort,
  membershipPlanHasCustomRates,
  type MembershipPlan,
  type MembershipPlanListResponse,
  type BillingFrequency,
  type PlanStatus,
  BILLING_FREQUENCY_LABELS,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import { EntityListCompactItem, EntitySplitListPanel } from '@/components/organisms';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import { MembershipPlanActions } from '@/components/organisms/pricebook/MembershipPlanActions';
import {
  DataTableDateRangeField,
  DataTableNumberFilterField,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  EntityListActiveDatePill,
  EntityListAdvancedFiltersSheet,
  EntityListCountFooter,
  EntityRowActions,
  type DataTableNumberOperator,
} from '@/components/molecules';

interface Props {
  selectedId?: string;
  onSelect: (id: string) => void;
  filters: MembershipPlanListFilters;
  onFiltersChange: (patch: Partial<MembershipPlanListFilters>) => void;
}

export type MembershipPlanStatusFilter = PlanStatus | 'all';
export type MembershipPlanFrequencyFilter = BillingFrequency | 'all';

export interface MembershipPlanListFilters {
  q: string;
  status: MembershipPlanStatusFilter;
  billingOp: DataTableNumberOperator;
  billingValue: string;
  discountOp: DataTableNumberOperator;
  discountValue: string;
  frequency: MembershipPlanFrequencyFilter;
  sort: number;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  updatedFrom: string | undefined;
  updatedTo: string | undefined;
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_MEMBERSHIP_PLAN_LIST_FILTERS: MembershipPlanListFilters = {
  q: '',
  status: 'all',
  billingOp: 'eq',
  billingValue: '',
  discountOp: 'eq',
  discountValue: '',
  frequency: 'all',
  sort: 0,
  dateFrom: undefined,
  dateTo: undefined,
  updatedFrom: undefined,
  updatedTo: undefined,
};

// eslint-disable-next-line react-refresh/only-export-components
export const MEMBERSHIP_PLAN_STATUS_FILTERS: Array<{
  label: string;
  value: MembershipPlanStatusFilter;
}> = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Archived', value: 'ARCHIVED' },
];

export const MEMBERSHIP_PLAN_SORT_OPTION_COUNT = 6;

const STATUS_DOT_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-500',
  ARCHIVED: 'bg-gray-400',
};

type SortOption = {
  label: string;
  sortBy: 'createdAt' | 'updatedAt' | 'name' | 'billing' | 'discount';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

const SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Name', sortBy: 'name', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Billing', sortBy: 'billing', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Discount', sortBy: 'discount', sortDir: 'desc', icon: <ArrowDown size={12} /> },
];

function matchesDateRange(value: string | undefined, range: DateRange | undefined): boolean {
  if (!range?.from && !range?.to) return true;
  if (!value) return false;

  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return false;

  if (range.from && time < range.from.getTime()) return false;
  if (range.to && time > endOfDay(range.to).getTime()) return false;
  return true;
}

function matchesNumberFilter(
  value: number | undefined,
  op: DataTableNumberOperator,
  raw: string,
): boolean {
  if (!raw.trim()) return true;
  const expected = Number(raw);
  if (!Number.isFinite(expected)) return true;
  const current = Number(value ?? 0);
  if (op === 'eq') return current === expected;
  if (op === 'gt') return current > expected;
  if (op === 'gte') return current >= expected;
  if (op === 'lt') return current < expected;
  return current <= expected;
}

function sortPlans(plans: MembershipPlan[], option: SortOption): MembershipPlan[] {
  return [...plans].sort((a, b) => {
    let comparison = 0;

    if (option.sortBy === 'billing') {
      comparison = membershipPlanBillingAmount(a) - membershipPlanBillingAmount(b);
    } else if (option.sortBy === 'discount') {
      comparison = membershipPlanDiscountPercent(a) - membershipPlanDiscountPercent(b);
    } else {
      const left = a[option.sortBy] ?? '';
      const right = b[option.sortBy] ?? '';
      comparison =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
    }

    return option.sortDir === 'asc' ? comparison : -comparison;
  });
}

/**
 * The list the panel actually renders: client-side status/billing/discount/
 * frequency/date filtering + sort, in the panel's exact order. Shared with the
 * index route's auto-select so its "first" matches the first visible row.
 */
export function selectVisibleMembershipPlans(
  plans: MembershipPlan[],
  filters: MembershipPlanListFilters,
): MembershipPlan[] {
  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
          to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
        }
      : undefined;
  const updatedRange: DateRange | undefined =
    filters.updatedFrom || filters.updatedTo
      ? {
          from: filters.updatedFrom ? parseISO(filters.updatedFrom) : undefined,
          to: filters.updatedTo ? parseISO(filters.updatedTo) : undefined,
        }
      : undefined;
  const activeSort = SORT_OPTIONS[filters.sort] ?? SORT_OPTIONS[0];

  return sortPlans(
    plans.filter((p) => {
      if (filters.status !== 'all' && p.status !== filters.status) return false;
      if (
        !matchesNumberFilter(
          membershipPlanBillingAmount(p),
          filters.billingOp,
          filters.billingValue,
        )
      ) {
        return false;
      }
      if (
        !matchesNumberFilter(
          membershipPlanDiscountPercent(p),
          filters.discountOp,
          filters.discountValue,
        )
      ) {
        return false;
      }
      if (filters.frequency !== 'all' && membershipPlanBillingFrequency(p) !== filters.frequency) {
        return false;
      }
      if (!matchesDateRange(p.createdAt, dateRange)) return false;
      if (!matchesDateRange(p.updatedAt, updatedRange)) return false;
      return true;
    }),
    activeSort,
  );
}

export function MembershipPlanListPanel({ selectedId, onSelect, filters, onFiltersChange }: Props) {
  const {
    searchInput: search,
    setSearchInput: setSearch,
    debouncedSearch,
  } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => onFiltersChange({ q: value }),
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeSort = SORT_OPTIONS[filters.sort] ?? SORT_OPTIONS[0];
  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
          to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
        }
      : undefined;
  const updatedRange: DateRange | undefined =
    filters.updatedFrom || filters.updatedTo
      ? {
          from: filters.updatedFrom ? parseISO(filters.updatedFrom) : undefined,
          to: filters.updatedTo ? parseISO(filters.updatedTo) : undefined,
        }
      : undefined;

  const { data, isLoading } = useApiQuery<MembershipPlanListResponse | MembershipPlan[]>({
    queryKey: membershipPlanKeys.list({ search: debouncedSearch || undefined }),
    queryFn: () =>
      apiGet<MembershipPlanListResponse | MembershipPlan[]>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlans(),
        debouncedSearch ? { params: { search: debouncedSearch } } : undefined,
      ),
  });

  const plans: MembershipPlan[] = Array.isArray(data)
    ? data
    : ((data as MembershipPlanListResponse)?.items ??
      (data as unknown as { data?: MembershipPlan[] })?.data ??
      []);

  const filtered = selectVisibleMembershipPlans(plans, filters);

  function clearDate() {
    onFiltersChange({ dateFrom: undefined, dateTo: undefined });
  }

  function clearUpdatedDate() {
    onFiltersChange({ updatedFrom: undefined, updatedTo: undefined });
  }

  const activeAdvancedCount =
    (filters.status !== 'all' ? 1 : 0) +
    (filters.billingValue.trim() ? 1 : 0) +
    (filters.discountValue.trim() ? 1 : 0) +
    (filters.frequency !== 'all' ? 1 : 0) +
    (dateRange?.from || dateRange?.to ? 1 : 0) +
    (updatedRange?.from || updatedRange?.to ? 1 : 0);

  function resetAdvancedFilters() {
    onFiltersChange({
      status: 'all',
      billingOp: 'eq',
      billingValue: '',
      discountOp: 'eq',
      discountValue: '',
      frequency: 'all',
      dateFrom: undefined,
      dateTo: undefined,
      updatedFrom: undefined,
      updatedTo: undefined,
    });
  }

  return (
    <EntitySplitListPanel
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search plans...',
      }}
      statusFilter={{
        value: filters.status,
        options: MEMBERSHIP_PLAN_STATUS_FILTERS,
        dotColors: STATUS_DOT_COLORS,
        onChange: (value) => onFiltersChange({ status: value as MembershipPlanStatusFilter }),
      }}
      sort={{
        activeIndex: filters.sort,
        activeLabel: activeSort.label,
        options: SORT_OPTIONS,
        onSelect: (sort) => onFiltersChange({ sort }),
        onReset: () => onFiltersChange({ sort: 0 }),
      }}
      toolbarEnd={
        <EntityListAdvancedFiltersSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          activeCount={activeAdvancedCount}
          onReset={resetAdvancedFilters}
          resetDisabled={activeAdvancedCount === 0}
        >
          <DataTableFilterSection
            label="Name"
            active={!!filters.q.trim()}
            onClear={() => onFiltersChange({ q: '' })}
          >
            <DataTableSearchFilterField
              value={filters.q}
              onValueChange={(value) => onFiltersChange({ q: value })}
              placeholder="Search by name…"
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Status"
            active={filters.status !== 'all'}
            onClear={() => onFiltersChange({ status: 'all' })}
          >
            <DataTableSelectFilterField
              value={filters.status}
              onValueChange={(value) =>
                onFiltersChange({ status: value as MembershipPlanStatusFilter })
              }
              placeholder="All statuses"
              options={MEMBERSHIP_PLAN_STATUS_FILTERS.map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All statuses' : filter.label,
              }))}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Billing Amount"
            active={!!filters.billingValue.trim()}
            onClear={() => onFiltersChange({ billingOp: 'eq', billingValue: '' })}
          >
            <DataTableNumberFilterField
              op={filters.billingOp}
              value={filters.billingValue}
              onOpChange={(op) => onFiltersChange({ billingOp: op })}
              onValueChange={(value) => onFiltersChange({ billingValue: value })}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Discount Value"
            active={!!filters.discountValue.trim()}
            onClear={() => onFiltersChange({ discountOp: 'eq', discountValue: '' })}
          >
            <DataTableNumberFilterField
              op={filters.discountOp}
              value={filters.discountValue}
              onOpChange={(op) => onFiltersChange({ discountOp: op })}
              onValueChange={(value) => onFiltersChange({ discountValue: value })}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Frequency"
            active={filters.frequency !== 'all'}
            onClear={() => onFiltersChange({ frequency: 'all' })}
          >
            <DataTableSelectFilterField
              value={filters.frequency}
              onValueChange={(value) =>
                onFiltersChange({ frequency: value as MembershipPlanFrequencyFilter })
              }
              placeholder="All frequencies"
              options={[
                { value: 'all', label: 'All frequencies' },
                ...(Object.entries(BILLING_FREQUENCY_LABELS) as [BillingFrequency, string][]).map(
                  ([value, label]) => ({ value, label }),
                ),
              ]}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Created At"
            active={!!(dateRange?.from || dateRange?.to)}
            onClear={clearDate}
          >
            <DataTableDateRangeField
              range={dateRange}
              onSelect={(range) =>
                onFiltersChange({
                  dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Updated At"
            active={!!(updatedRange?.from || updatedRange?.to)}
            onClear={clearUpdatedDate}
          >
            <DataTableDateRangeField
              range={updatedRange}
              onSelect={(range) =>
                onFiltersChange({
                  updatedFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                  updatedTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                })
              }
            />
          </DataTableFilterSection>
        </EntityListAdvancedFiltersSheet>
      }
      belowToolbar={<EntityListActiveDatePill range={dateRange} onClear={clearDate} />}
      isLoading={isLoading}
      emptyIcon={CreditCard}
      emptyTitle={plans.length === 0 ? 'No plans yet' : 'No plans match'}
      hasItems={filtered.length > 0}
      skeletonCount={6}
      footer={!isLoading ? <EntityListCountFooter count={filtered.length} singular="plan" /> : null}
    >
      {filtered.map((plan) => {
        const isSelected = plan.id === selectedId;
        const billingAmount = membershipPlanBillingAmount(plan);
        const hasBilling = billingAmount > 0;
        const hasDiscount = hasMembershipPlanDiscount(plan);
        const hasCustomRates = membershipPlanHasCustomRates(plan);
        const discountSummary = hasCustomRates
          ? formatMembershipPlanCustomRatesSummary(plan)
          : hasDiscount
            ? `${formatMembershipPlanDiscount(plan)} off`
            : null;

        return (
          <EntityListCompactItem
            key={plan.id}
            selected={isSelected}
            onClick={() => plan.id && onSelect(plan.id)}
            content={
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-1.5">
                  <p className="text-sm font-medium truncate">{plan.name}</p>
                  <span
                    className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                      plan.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {plan.status}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  {hasBilling && (
                    <span className="text-xs text-muted-foreground">
                      {formatCurrency(billingAmount)}
                      {formatBillingIntervalShort(
                        plan.billingIntervalValue,
                        plan.billingIntervalUnit,
                      )}
                    </span>
                  )}
                  {hasBilling && discountSummary && (
                    <span className="text-xs text-muted-foreground">·</span>
                  )}
                  {discountSummary ? (
                    <span className="text-xs text-muted-foreground">{discountSummary}</span>
                  ) : null}
                </div>
              </div>
            }
            actions={
              plan.id ? (
                <EntityRowActions>
                  <MembershipPlanActions id={plan.id} name={plan.name} variant="row" />
                </EntityRowActions>
              ) : null
            }
          />
        );
      })}
    </EntitySplitListPanel>
  );
}
